import React, { useMemo, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Dropdown, type DropdownItem, Loader } from '@gr-monorepo/common/public/ui';
import { useClickStreamContext } from 'features/shared/ClickStream';
import { useOlympiadsFormControl } from './OlympiadsFormControl';
import OlympiadCard from './components/OlympiadCard/OlympiadCard';
import OlympiadsAccessNotice from './components/OlympiadsAccessNotice/OlympiadsAccessNotice';
import OlympiadsStatusFilters from './components/OlympiadsStatusFilters/OlympiadsStatusFilters';
import OlympiadsManagerCard from './components/OlympiadsManagerCard/OlympiadsManagerCard';
import OlympiadsStatisticsUnavailable from './components/OlympiadsStatisticsUnavailable/OlympiadsStatisticsUnavailable';
import {
  getOlympiadEducationYearOption,
  isOlympiadStatisticsUnavailableYear,
  isTableStatisticYear,
  olympiadEducationYearOptions,
  readSelectedOlympiadYear,
  saveSelectedOlympiadYear,
} from './constants/years';
import {
  Olympiad,
  OlympiadStatus,
  defaultOlympiadStatusesFilter,
  olympiadStatusFilterOrder,
  toggleOlympiadStatusFilter,
} from './models/Olympiad';
import { useAuthContext } from 'hooks/useAuthContext';
import * as S from './OlympiadsListStyled';
import { H1 } from 'features/shared/ui/Heading';

const MAIN_PAGE_PATH = '/main';

const STATUS_ORDER: Record<OlympiadStatus, number> = {
  [OlympiadStatus.current]: 0,
  [OlympiadStatus.passed]: 1,
  [OlympiadStatus.future]: 2,
};
const FILTERS_VISIBLE_FROM_MONTH = 9;

const OlympiadsList = () => {
  const history = useHistory();
  const { logPageEvent } = useClickStreamContext();
  const { authorized } = useAuthContext();

  const [selectedYear, setSelectedYear] = useState(() => readSelectedOlympiadYear());
  const [selectedStatuses, setSelectedStatuses] = useState<OlympiadStatus[]>(defaultOlympiadStatusesFilter);

  const isUnavailable = isOlympiadStatisticsUnavailableYear(selectedYear);
  const isPrevYear = isTableStatisticYear(selectedYear);
  const currentCalendarYear = String(new Date().getFullYear());
  const shouldHideFiltersByMonth =
    selectedYear[0] === currentCalendarYear &&
    new Date().getMonth() + 1 < FILTERS_VISIBLE_FROM_MONTH;
  const selectedYearOption = getOlympiadEducationYearOption(selectedYear);

  const statusesForRequest = isPrevYear ? [OlympiadStatus.passed] : selectedStatuses;
  const yearsForRequest = selectedYear;

  const { olympiads, loading } = useOlympiadsFormControl(statusesForRequest, yearsForRequest, !isUnavailable);

  const toggleStatusFilter = useCallback((status: OlympiadStatus) => {
    logPageEvent?.({
      eventType: 'click',
      eventInfo: `button_olympiad_filter_${status}`,
      cd1: status,
    });
    setSelectedStatuses((prev) => toggleOlympiadStatusFilter(prev, status));
  }, [logPageEvent]);

  const handleYearChange = (option: DropdownItem) => {
    const yearOption = olympiadEducationYearOptions.find((item) => item.value === option.value);
    if (!yearOption) return;

    if (yearOption.year[0] !== selectedYear[0]) {
      setSelectedStatuses(defaultOlympiadStatusesFilter);
    }

    setSelectedYear(yearOption.year);
    saveSelectedOlympiadYear(yearOption.year);
    logPageEvent?.({
      eventType: 'olymp_change_year',
      eventInfo: `Олимпиада: Клик по табу с выбором уч. года года ${option.label}`,
    });
  };

  const handleCardClick = (olympiad: Olympiad) => {
    logPageEvent?.({
      eventType: 'click',
      eventInfo: `button_olympiad_${olympiad.olympiad_id}`,
      cd1: olympiad.olympiad_id,
    });
    saveSelectedOlympiadYear(selectedYear);
    history.push({
      pathname: `${MAIN_PAGE_PATH}/olympiads/${olympiad.olympiad_id}/statistic`,
      state: { olympiad, selectedYear },
    });
  };

  const sortedOlympiads = useMemo(
    () =>
      [...olympiads].sort((a, b) => {
        const diff = STATUS_ORDER[a.status as OlympiadStatus] - STATUS_ORDER[b.status as OlympiadStatus];
        if (diff !== 0) return diff;

        const timeA = a.olympiad_start ? Date.parse(a.olympiad_start) : Infinity;
        const timeB = b.olympiad_start ? Date.parse(b.olympiad_start) : Infinity;

        return a.status === OlympiadStatus.future ? timeA - timeB : timeB - timeA;
      }),
    [olympiads],
  );

  if (loading && olympiads.length === 0 && !isUnavailable) {
    return (
      <S.OlympiadsPage data-testid="olympiads-list-page">
        <Loader size={56} />
      </S.OlympiadsPage>
    );
  }

  return (
    <S.OlympiadsPage data-testid="olympiads-list-page">
      <S.OlympiadsPageHeader>
        <H1>Олимпиады</H1>
        <S.OlympiadsYearDropdown>
          <Dropdown
            value={selectedYearOption}
            options={olympiadEducationYearOptions}
            onChange={handleYearChange}
            dataTestId="olympiads-year-dropdown"
          />
        </S.OlympiadsYearDropdown>
      </S.OlympiadsPageHeader>

      <S.OlympiadsContent $stretch={isUnavailable}>
        <S.OlympiadsMain>
          {!authorized && !isUnavailable && <OlympiadsAccessNotice />}

          {isUnavailable ? (
            <OlympiadsStatisticsUnavailable />
          ) : (
            <S.OlympiadsWrapper data-testid="olympiads-list">
              {loading ? (
                <S.OlympiadsListLoader data-testid="olympiads-list-loader">
                  <Loader size={56} />
                </S.OlympiadsListLoader>
              ) : sortedOlympiads.length === 0 ? (
                <OlympiadsStatisticsUnavailable />
              ) : (
                sortedOlympiads.map((item) => (
                  <OlympiadCard
                    key={item.olympiad_id || item.name}
                    olympiad={item}
                    showWarningMessage={item.status === OlympiadStatus.future}
                    showInformButton={authorized}
                    onClick={authorized ? () => handleCardClick(item) : undefined}
                    data-testid={`olympiad-card-${item.olympiad_id ?? item.name}`}
                  />
                ))
              )}
            </S.OlympiadsWrapper>
          )}
        </S.OlympiadsMain>

        {!isUnavailable && (
          <S.OlympiadsSidebar>
            {!isPrevYear && !shouldHideFiltersByMonth && (
              <OlympiadsStatusFilters
                availableStatuses={olympiadStatusFilterOrder}
                selectedStatuses={selectedStatuses}
                onToggle={toggleStatusFilter}
              />
            )}
            {authorized && <OlympiadsManagerCard />}
          </S.OlympiadsSidebar>
        )}
      </S.OlympiadsContent>
    </S.OlympiadsPage>
  );
};

export default OlympiadsList;